import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Instagram, Link2, Loader2, Sparkles, Unlink, Upload, Youtube } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';
import { disconnectYouTube, useYouTubeAccountReset } from '@/lib/youtubeConnection';
import { formatApiError } from '@/lib/formatApiError';
import { uploadFileViaPresignedUrl } from '@/lib/exportMediaStaging';
import { autofillFromUploadedVideo, watchDiscoverySeo } from '@/lib/publishVideoAutofill';
import { SocialPublishHistoryPanel } from '@/components/social/SocialPublishHistoryPanel';
import { PublishThumbnailPicker } from '@/components/social/PublishThumbnailPicker';
import { PublishTagChips, parsePublishTags } from '@/components/social/PublishTagChips';
import { SeoChecklist } from '@/components/channel/SeoChecklist';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const fieldInputClass = 'rounded-xl border border-white/10 bg-[#0c1018] text-white text-sm h-10';
const fieldInputStyle = undefined;
const fieldTextareaClass = 'w-full rounded-xl border border-white/10 bg-[#0c1018] px-3 py-2.5 text-sm text-white outline-none focus:border-[#34d399]/40 resize-y';

const DEFAULT_CATEGORIES = [
  { id: '22', label: 'People & Blogs' },
  { id: '27', label: 'Education' },
  { id: '24', label: 'Entertainment' },
  { id: '23', label: 'Comedy' },
];

export const SocialPublishPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('youtube');

  const [ytStatus, setYtStatus] = useState({ connected: false, channel_title: null });
  const [ytStatusLoading, setYtStatusLoading] = useState(true);
  const [ytBeginBusy, setYtBeginBusy] = useState(false);
  const [ytDisconnectBusy, setYtDisconnectBusy] = useState(false);
  const [ytUploadBusy, setYtUploadBusy] = useState(false);
  const [ytAiBusy, setYtAiBusy] = useState(false);
  const [ytAutofillNote, setYtAutofillNote] = useState('');
  const [thumbFrameUrls, setThumbFrameUrls] = useState([]);
  const [aiThumbUrls, setAiThumbUrls] = useState([]);
  const [metadataOptimizeLang, setMetadataOptimizeLang] = useState('en');
  const [youtubeShortsMode, setYoutubeShortsMode] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  const [igStatus, setIgStatus] = useState({ connected: false, username: null });
  const [igStatusLoading, setIgStatusLoading] = useState(true);
  const [igBeginBusy, setIgBeginBusy] = useState(false);
  const [igDisconnectBusy, setIgDisconnectBusy] = useState(false);
  const [igUploadBusy, setIgUploadBusy] = useState(false);
  const [igAiBusy, setIgAiBusy] = useState(false);
  const [igAutofillNote, setIgAutofillNote] = useState('');
  const [igCoverUrl, setIgCoverUrl] = useState('');
  const [igCoverFrameUrls, setIgCoverFrameUrls] = useState([]);
  const [igAiThumbUrls, setIgAiThumbUrls] = useState([]);

  const [videoUrl, setVideoUrl] = useState('');
  const [videoUploadBusy, setVideoUploadBusy] = useState(false);
  const videoFileRef = useRef(null);
  const videoUrlRef = useRef('');
  const seenVideoUrl = useRef('');
  const ytAutofillGen = useRef(0);
  const igAutofillGen = useRef(0);
  videoUrlRef.current = videoUrl;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tagsRaw, setTagsRaw] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [moreTagsBusy, setMoreTagsBusy] = useState(false);
  const [categoryId, setCategoryId] = useState('22');
  const [privacyStatus, setPrivacyStatus] = useState('private');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState('');
  const [defaultAudioLanguage, setDefaultAudioLanguage] = useState('');
  const [playlistId, setPlaylistId] = useState('');
  const [notifySubscribers, setNotifySubscribers] = useState(false);
  const [embeddable, setEmbeddable] = useState(true);
  const [license, setLicense] = useState('youtube');
  const [madeForKids, setMadeForKids] = useState(false);

  const [igCaption, setIgCaption] = useState('');
  const [igShareToFeed, setIgShareToFeed] = useState(true);
  const [igReelsMode, setIgReelsMode] = useState(true);
  const [igLang, setIgLang] = useState('en');

  const [ytHistory, setYtHistory] = useState([]);
  const [igHistory, setIgHistory] = useState([]);
  const [ytHistoryLoading, setYtHistoryLoading] = useState(true);
  const [igHistoryLoading, setIgHistoryLoading] = useState(true);

  const loadPublishHistory = useCallback(async (platform) => {
    const setLoading = platform === 'youtube' ? setYtHistoryLoading : setIgHistoryLoading;
    const setItems = platform === 'youtube' ? setYtHistory : setIgHistory;
    setLoading(true);
    try {
      const { data } = await api.get('/social-publish/history', { params: { platform, limit: 40 } });
      const items = Array.isArray(data?.items) ? data.items : [];
      setItems(items);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadYtStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/social-publish/youtube/status');
      setYtStatus({
        connected: Boolean(data.connected),
        channel_title: data.channel_title || null,
      });
    } catch {
      setYtStatus({ connected: false, channel_title: null });
    } finally {
      setYtStatusLoading(false);
    }
  }, []);

  useYouTubeAccountReset(loadYtStatus);

  const loadIgStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/social-publish/instagram/status');
      setIgStatus({
        connected: Boolean(data.connected),
        username: data.username || null,
        page_name: data.page_name || null,
      });
    } catch {
      setIgStatus({ connected: false, username: null });
    } finally {
      setIgStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    loadYtStatus();
    loadIgStatus();
    loadPublishHistory('youtube');
    loadPublishHistory('instagram');
    const params = new URLSearchParams(window.location.search);
    const prefillVideo = String(location.state?.videoUrl || params.get('video_url') || params.get('videoUrl') || '').trim();
    if (prefillVideo) setVideoUrl(prefillVideo);
    const prefillTitle = params.get('title');
    const prefillDescription = params.get('description');
    const prefillTags = params.get('tags');
    if (prefillTitle) setTitle(prefillTitle.slice(0, 100));
    if (prefillDescription) setDescription(prefillDescription.slice(0, 5000));
    if (prefillTags) setTagsRaw(prefillTags);
    const yt = params.get('youtube');
    const ig = params.get('instagram');
    const msg = params.get('message');
    if (yt === 'connected') {
      toast.success('YouTube connected.');
      setActiveTab('youtube');
      loadYtStatus();
    } else if (yt === 'error') {
      toast.error(msg ? decodeURIComponent(msg) : 'YouTube connection failed');
      setActiveTab('youtube');
    }
    if (ig === 'connected') {
      toast.success('Instagram connected.');
      setActiveTab('instagram');
      loadIgStatus();
    } else if (ig === 'error') {
      toast.error(msg ? decodeURIComponent(msg) : 'Instagram connection failed');
      setActiveTab('instagram');
    }
    if (yt || ig) {
      window.history.replaceState({}, '', '/social-publish');
    }
  }, [loadYtStatus, loadIgStatus, loadPublishHistory, location.state]);

  useEffect(() => {
    const next = videoUrl.trim();
    const prev = seenVideoUrl.current;
    seenVideoUrl.current = next;
    if (!prev || !next || prev === next) return;
    ytAutofillGen.current += 1;
    igAutofillGen.current += 1;
    setYtAiBusy(false);
    setIgAiBusy(false);
    setTitle('');
    setDescription('');
    setTagsRaw('');
    setTagSuggestions([]);
    setThumbnailUrl('');
    setAiThumbUrls([]);
    setThumbFrameUrls([]);
    setYtAutofillNote('');
    setIgCaption('');
    setIgCoverUrl('');
    setIgAiThumbUrls([]);
    setIgCoverFrameUrls([]);
    setIgAutofillNote('');
  }, [videoUrl]);

  useEffect(() => {
    loadPublishHistory(activeTab);
  }, [activeTab, loadPublishHistory]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/social-publish/youtube/categories');
        if (Array.isArray(data.categories) && data.categories.length) {
          setCategories(data.categories.map((c) => ({ id: c.id, label: `${c.label} (${c.id})` })));
        }
      } catch {
        /* defaults */
      }
    })();
  }, []);

  const handleConnectYouTube = async () => {
    setYtBeginBusy(true);
    try {
      const { data } = await api.post('/social-publish/youtube/oauth/begin', {
        return_path: '/social-publish',
      });
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
        return;
      }
      toast.error('Could not start YouTube login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OAuth not configured or failed');
    } finally {
      setYtBeginBusy(false);
    }
  };

  const handleConnectInstagram = async () => {
    setIgBeginBusy(true);
    try {
      const { data } = await api.post('/social-publish/instagram/oauth/begin', {
        return_path: '/social-publish',
      });
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
        return;
      }
      toast.error('Could not start Instagram login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OAuth not configured or failed');
    } finally {
      setIgBeginBusy(false);
    }
  };

  const handleYtDisconnect = async () => {
    setYtDisconnectBusy(true);
    try {
      await disconnectYouTube();
      toast.success('YouTube disconnected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disconnect failed');
    } finally {
      setYtDisconnectBusy(false);
    }
  };

  const handleIgDisconnect = async () => {
    setIgDisconnectBusy(true);
    try {
      await api.delete('/social-publish/instagram/disconnect');
      toast.success('Instagram disconnected');
      await loadIgStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disconnect failed');
    } finally {
      setIgDisconnectBusy(false);
    }
  };

  const handleLocalVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.type && !file.type.startsWith('video/')) {
      toast.error('Choose a video file');
      return;
    }
    setVideoUploadBusy(true);
    try {
      const url = String(await uploadFileViaPresignedUrl(file) || '').trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        throw new Error('Upload finished without a public video link');
      }
      setVideoUrl(url);
      toast.success('Video uploaded. Add a title, then publish.');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not upload the video'));
    } finally {
      setVideoUploadBusy(false);
    }
  };

  const handleMoreTags = async () => {
    setMoreTagsBusy(true);
    try {
      const existing = [...parsePublishTags(tagsRaw), ...tagSuggestions];
      const { data } = await api.post('/social-publish/youtube/suggest-tags', {
        title: (title || '').trim().slice(0, 120),
        description: (description || '').trim().slice(0, 2000),
        existing_tags: existing,
        language: (defaultLanguage || metadataOptimizeLang || 'en').trim().slice(0, 12) || 'en',
        count: 8,
      });
      const extra = Array.isArray(data.tags) ? data.tags.map((tag) => String(tag || '').trim()).filter(Boolean) : [];
      if (!extra.length) {
        toast.info('No extra tags found. Try Autofill first.');
        return;
      }
      const have = new Set(existing.map((tag) => tag.toLocaleLowerCase()));
      setTagSuggestions((prev) => {
        const next = [...prev];
        extra.forEach((tag) => {
          if (!have.has(tag.toLocaleLowerCase())) {
            have.add(tag.toLocaleLowerCase());
            next.push(tag);
          }
        });
        return next;
      });
    } catch (err) {
      toast.error(formatApiError(err, 'Could not suggest more tags'));
    } finally {
      setMoreTagsBusy(false);
    }
  };

  const handleYoutubeUpload = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim() || !title.trim()) {
      toast.error('Video URL and title are required');
      return;
    }
    const tags = parsePublishTags(tagsRaw);
    setYtUploadBusy(true);
    try {
      const { data } = await api.post('/social-publish/youtube/upload', {
        video_url: videoUrl.trim(),
        title: title.trim(),
        description: description.trim(),
        tags,
        category_id: categoryId,
        privacy_status: privacyStatus,
        thumbnail_url: thumbnailUrl.trim() || null,
        default_language: defaultLanguage.trim() || null,
        default_audio_language: defaultAudioLanguage.trim() || null,
        playlist_id: playlistId.trim() || null,
        notify_subscribers: notifySubscribers,
        embeddable,
        license,
        made_for_kids: madeForKids,
        shorts_metadata_boost: youtubeShortsMode,
      });
      toast.success(`Uploaded to YouTube: ${data.url}`);
      if (Array.isArray(data.warnings)) data.warnings.forEach((w) => toast.warning(w));
      await loadPublishHistory('youtube');
    } catch (err) {
      if (err.response?.status === 401) {
        await loadYtStatus();
      }
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setYtUploadBusy(false);
    }
  };

  const handleInstagramUpload = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim() || !igCaption.trim()) {
      toast.error('Video URL and caption are required');
      return;
    }
    setIgUploadBusy(true);
    try {
      const { data } = await api.post('/social-publish/instagram/upload', {
        video_url: videoUrl.trim(),
        caption: igCaption.trim(),
        share_to_feed: igShareToFeed,
        cover_url: igCoverUrl.trim() || null,
      });
      toast.success(data.permalink ? `Published: ${data.permalink}` : 'Published to Instagram');
      if (Array.isArray(data.warnings)) data.warnings.forEach((w) => toast.warning(w));
      await loadPublishHistory('instagram');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setIgUploadBusy(false);
    }
  };

  const handleYtAutofill = async () => {
    const gen = ++ytAutofillGen.current;
    const uploaded = videoUrlRef.current.trim();
    setYtAiBusy(true);
    setYtAutofillNote(uploaded ? 'Queued — transcribing this video…' : '');
    try {
      const hint = uploaded ? '' : (title || '').trim();
      let data;
      if (uploaded) {
        setTitle('');
        setDescription('');
        setTagsRaw('');
        setTagSuggestions([]);
        setThumbnailUrl('');
        setAiThumbUrls([]);
        data = await autofillFromUploadedVideo(
          {
            video_url: uploaded,
            platform: 'youtube',
            title_hint: '',
            script_text: '',
            language: (metadataOptimizeLang || 'en').trim().slice(0, 12) || 'en',
            optimize_for_shorts: Boolean(youtubeShortsMode),
          },
          {
            isCurrent: () => ytAutofillGen.current === gen && videoUrlRef.current.trim() === uploaded,
            onProgress: (row) => {
              if (ytAutofillGen.current === gen) setYtAutofillNote(row.message || 'Working…');
            },
          },
        );
      } else {
        const response = await api.post('/social-publish/youtube/suggest-metadata', {
          title_hint: hint.slice(0, 300),
          script_text: description.trim().slice(0, 14000),
          language: (metadataOptimizeLang || 'en').trim().slice(0, 12) || 'en',
          optimize_for_shorts: Boolean(youtubeShortsMode),
        });
        data = response.data;
      }
      if (ytAutofillGen.current !== gen || (uploaded && videoUrlRef.current.trim() !== uploaded)) return;
      const appliedTitle = data.title ? String(data.title).slice(0, 100) : hint;
      const appliedDescription = data.description != null
        ? String(data.description).slice(0, 5000)
        : (uploaded ? '' : description);
      if (data.title) setTitle(appliedTitle);
      if (data.description != null) setDescription(appliedDescription);
      if (Array.isArray(data.tags) && data.tags.length) setTagsRaw(data.tags.join(', '));
      if (Array.isArray(data.keywords) && data.keywords.length) {
        const selectedKeys = new Set((data.tags || []).map((tag) => String(tag).trim().toLocaleLowerCase()));
        setTagSuggestions(
          data.keywords
            .map((tag) => String(tag || '').trim())
            .filter((tag) => tag && !selectedKeys.has(tag.toLocaleLowerCase())),
        );
      } else {
        setTagSuggestions([]);
      }
      if (data.category_id) setCategoryId(String(data.category_id));
      if (data.default_language) setDefaultLanguage(String(data.default_language).slice(0, 12));
      if (data.default_audio_language) setDefaultAudioLanguage(String(data.default_audio_language).slice(0, 12));
      const keywordLine = Array.isArray(data.keywords) && data.keywords.length
        ? ` Keywords: ${data.keywords.slice(0, 8).join(', ')}.`
        : '';
      const tipLine = Array.isArray(data.seo_tips) && data.seo_tips.length
        ? data.seo_tips.slice(0, 3).join(' ')
        : '';
      const scoreBit = Number.isFinite(Number(data.seo_score)) ? ` SEO score ${data.seo_score}/100.` : '';
      setYtAutofillNote([tipLine, keywordLine.trim(), scoreBit.trim()].filter(Boolean).join(' '));
      setYtAiBusy(false);
      if (uploaded) {
        const baseline = {
          title: appliedTitle,
          description: appliedDescription,
          tags: Array.isArray(data.tags) ? data.tags.join(', ') : '',
        };
        watchDiscoverySeo(uploaded, {
          isCurrent: () => ytAutofillGen.current === gen && videoUrlRef.current.trim() === uploaded,
          onPack: (seo) => {
            if (seo.title) {
              setTitle((current) => (current === baseline.title ? String(seo.title).slice(0, 100) : current));
            }
            if (seo.description) {
              setDescription((current) => (current === baseline.description ? String(seo.description).slice(0, 5000) : current));
            }
            if (Array.isArray(seo.tags) && seo.tags.length) {
              const nextTags = seo.tags.join(', ');
              setTagsRaw((current) => (current === baseline.tags ? nextTags : current));
            }
          },
        });
      }
      toast.success(`Form autofilled with AI.${uploaded ? ' Used this video’s transcript.' : ''} Review before uploading.`);
      if (appliedTitle.trim()) {
        const infoLine = (appliedDescription || '').trim().split('\n')[0]?.slice(0, 200) || '';
        const excerpt = (data.transcript || appliedDescription || '').trim().slice(0, 2000);
        void (async () => {
          try {
            const { data: thumbData } = await api.post('/social-publish/ai-thumbnail-options', {
              platform: 'youtube',
              title: appliedTitle.trim(),
              info_line: infoLine,
              script_excerpt: excerpt,
              count: 3,
            });
            if (ytAutofillGen.current !== gen) return;
            const urls = Array.isArray(thumbData.urls) ? thumbData.urls.filter(Boolean) : [];
            setAiThumbUrls(urls);
            if (urls[0]) setThumbnailUrl(urls[0]);
          } catch {
            if (ytAutofillGen.current !== gen) return;
            setAiThumbUrls([]);
          }
        })();
      }
    } catch (err) {
      if (err?.cancelled) return;
      if (ytAutofillGen.current !== gen) return;
      setYtAutofillNote('');
      toast.error(formatApiError(err, 'AI autofill failed'));
    } finally {
      if (ytAutofillGen.current === gen) setYtAiBusy(false);
    }
  };

  const handleIgAutofill = async () => {
    const gen = ++igAutofillGen.current;
    const uploaded = videoUrlRef.current.trim();
    setIgAiBusy(true);
    setIgAutofillNote(uploaded ? 'Queued — transcribing this video…' : '');
    try {
      const hint = uploaded ? '' : (title || igCaption || '').trim().split('\n')[0];
      let data;
      if (uploaded) {
        setIgCaption('');
        setIgCoverUrl('');
        setIgAiThumbUrls([]);
        data = await autofillFromUploadedVideo(
          {
            video_url: uploaded,
            platform: 'instagram',
            title_hint: '',
            script_text: '',
            language: (igLang || metadataOptimizeLang || 'en').trim().slice(0, 12) || 'en',
            optimize_for_reels: igReelsMode,
          },
          {
            isCurrent: () => igAutofillGen.current === gen && videoUrlRef.current.trim() === uploaded,
            onProgress: (row) => {
              if (igAutofillGen.current === gen) setIgAutofillNote(row.message || 'Working…');
            },
          },
        );
      } else {
        const response = await api.post('/social-publish/instagram/suggest-metadata', {
          title_hint: hint,
          script_text: (description || igCaption || '').trim(),
          language: (igLang || metadataOptimizeLang || 'en').trim().slice(0, 12) || 'en',
          optimize_for_reels: igReelsMode,
        });
        data = response.data;
      }
      if (igAutofillGen.current !== gen || (uploaded && videoUrlRef.current.trim() !== uploaded)) return;
      const appliedCaption = data.caption ? String(data.caption).slice(0, 2200) : (uploaded ? '' : igCaption);
      if (data.caption) setIgCaption(appliedCaption);
      const keywordLine = Array.isArray(data.keywords) && data.keywords.length
        ? `Keywords: ${data.keywords.slice(0, 8).join(', ')}.`
        : '';
      const tipLine = Array.isArray(data.seo_tips) && data.seo_tips[0] ? data.seo_tips.slice(0, 2).join(' ') : '';
      setIgAutofillNote([keywordLine, tipLine].filter(Boolean).join(' '));
      setIgAiBusy(false);
      toast.success(`Form autofilled with AI.${uploaded ? ' Used this video’s transcript.' : ''} Review before publishing.`);
      const coverTitle = (appliedCaption || hint).trim().split('\n')[0];
      if (coverTitle) {
        void (async () => {
          try {
            const { data: thumbData } = await api.post('/social-publish/ai-thumbnail-options', {
              platform: 'instagram',
              title: coverTitle,
              info_line: coverTitle.slice(0, 200),
              script_excerpt: (data.transcript || appliedCaption || '').trim().slice(0, 2000),
              count: 3,
            });
            if (igAutofillGen.current !== gen) return;
            const urls = Array.isArray(thumbData.urls) ? thumbData.urls.filter(Boolean) : [];
            setIgAiThumbUrls(urls);
            if (urls[0]) setIgCoverUrl(urls[0]);
          } catch {
            if (igAutofillGen.current !== gen) return;
            setIgAiThumbUrls([]);
          }
        })();
      }
    } catch (err) {
      if (err?.cancelled) return;
      if (igAutofillGen.current !== gen) return;
      setIgAutofillNote('');
      toast.error(err.response?.data?.detail || 'AI autofill failed');
    } finally {
      if (igAutofillGen.current === gen) setIgAiBusy(false);
    }
  };

  const renderAccountBar = ({
    platform,
    loading,
    connected,
    connectedLabel,
    onConnect,
    onDisconnect,
    connectBusy,
    disconnectBusy,
    connectLabel,
    connectClassName,
    connectStyle,
    hint,
  }) => (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 rounded-2xl bg-white/[0.04]">
      <div className="flex items-start gap-3 min-w-0">
        {platform === 'youtube' ? (
          <Youtube className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
        ) : (
          <Instagram className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#E1306C' }} />
        )}
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-[#8b95a7]">Account</p>
          {loading ? (
            <div className="flex items-center gap-2 text-sm theme-text-secondary">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking connection…
            </div>
          ) : connected ? (
            <p className="text-sm theme-text-primary truncate">
              Connected as <span className="font-semibold">{connectedLabel}</span>
            </p>
          ) : (
            <p className="text-xs theme-text-tertiary">{hint}</p>
          )}
        </div>
      </div>
      {!loading && (
        connected ? (
          <Button type="button" variant="outline" size="sm" disabled={disconnectBusy} onClick={onDisconnect} className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/10 gap-1 shrink-0">
            {disconnectBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
            Disconnect
          </Button>
        ) : (
          <Button type="button" onClick={onConnect} disabled={connectBusy} className={`dashboard-btn gap-2 shrink-0 ${connectClassName || ''}`} style={connectStyle}>
            {connectBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {connectLabel}
          </Button>
        )
      )}
    </div>
  );

  const hasPublishVideo = Boolean(videoUrl.trim());
  const aiOptionsClass = 'rounded-2xl bg-white/[0.04] p-4 space-y-3';
  const aiOptionsStyle = undefined;

  return (
    <div className="w-full max-w-none px-3 sm:px-8 py-5 pb-16 font-jakarta text-white overflow-x-hidden" data-testid="social-publish-page">
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-white">Publish</h1>
            <p className="text-sm text-[#8b95a7] mt-1">
              Add a title and details, then send this video to YouTube or Instagram.
            </p>
          </div>
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#34d399] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
        </div>

        <button
          type="button"
          onClick={() => {
            const next = videoUrl.trim();
            navigate(next ? `/social-publish/auto?video=${encodeURIComponent(next)}` : '/social-publish/auto');
          }}
          className="group flex w-full items-center gap-4 rounded-[22px] px-5 py-5 text-left bg-gradient-to-r from-[#c4b5fd] via-[#a78bfa] to-[#67e8f9] text-[#080d14] shadow-lg shadow-[#a78bfa]/25 hover:brightness-110 active:scale-[0.99] transition-all cursor-pointer min-h-[92px]"
          data-testid="social-publish-auto-button"
        >
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-black/15">
            <Sparkles className="w-6 h-6" />
          </span>
          <span className="min-w-0">
            <span className="block text-lg sm:text-xl font-extrabold tracking-tight">Automate All</span>
            <span className="block text-sm font-semibold text-[#080d14]/75">
              SEO + auto publish. A video on this page is carried into the runner.
            </span>
          </span>
        </button>

        <div className="rounded-[18px] bg-[#1c2433] p-5 sm:p-6 space-y-5">
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
              <div className="min-w-0">
                <Label htmlFor="sp-shared-video" className="text-sm font-semibold text-white">
                  Video
                </Label>
                <p className="text-xs text-[#8b95a7] mt-1">Upload a file, or paste a link.</p>
              </div>
              <Button
                type="button"
                disabled={videoUploadBusy}
                onClick={() => videoFileRef.current?.click()}
                className="rounded-full px-5 py-2 h-auto gap-2 bg-[#34d399] text-[#06211b] font-bold hover:brightness-105 shrink-0"
                data-testid="sp-upload-video"
              >
                {videoUploadBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {videoUploadBusy ? 'Uploading…' : 'Upload video'}
              </Button>
            </div>
            <Input
              id="sp-shared-video"
              placeholder="https://…/your-video.mp4"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className={fieldInputClass}
              disabled={videoUploadBusy}
            />
            <input
              ref={videoFileRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm,video/*"
              className="hidden"
              tabIndex={-1}
              onChange={handleLocalVideoUpload}
            />
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="!h-auto w-fit gap-2 !bg-transparent !p-0">
              <TabsTrigger
                value="youtube"
                className="!rounded-full gap-2 px-4 py-2 text-sm font-semibold text-[#9aa6b8] data-[state=active]:!bg-white data-[state=active]:!text-[#10141c] data-[state=active]:!shadow-none"
              >
                <Youtube className="w-4 h-4" /> YouTube
              </TabsTrigger>
              <TabsTrigger
                value="instagram"
                className="!rounded-full gap-2 px-4 py-2 text-sm font-semibold text-[#9aa6b8] data-[state=active]:!bg-white data-[state=active]:!text-[#10141c] data-[state=active]:!shadow-none"
              >
                <Instagram className="w-4 h-4" /> Instagram
              </TabsTrigger>
            </TabsList>

            <TabsContent value="youtube" className="mt-4">
              <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(300px,380px)] items-start gap-5">
                <div className="flex flex-col min-w-0 gap-4">
                  {renderAccountBar({
                    platform: 'youtube',
                    loading: ytStatusLoading,
                    connected: ytStatus.connected,
                    connectedLabel: ytStatus.channel_title || 'your channel',
                    onConnect: handleConnectYouTube,
                    onDisconnect: handleYtDisconnect,
                    connectBusy: ytBeginBusy,
                    disconnectBusy: ytDisconnectBusy,
                    connectLabel: 'Connect YouTube',
                    connectClassName: 'bg-red-600 hover:bg-red-700 text-white border-0',
                    hint: 'Connect your channel to upload videos',
                  })}

                  <form onSubmit={handleYoutubeUpload} className="flex flex-col gap-4">
                    <div className={aiOptionsClass} style={aiOptionsStyle}>
                      <p className="text-sm font-semibold text-white">Fill this in for me</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Title / description language</Label>
                          <Select value={metadataOptimizeLang} onValueChange={setMetadataOptimizeLang}>
                            <SelectTrigger className={fieldInputClass} style={fieldInputStyle} data-testid="sp-youtube-metadata-lang">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="en">English</SelectItem>
                              <SelectItem value="hi">Hindi (हिंदी)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <label className="flex flex-col justify-center gap-1.5 rounded-xl bg-[#0c1018] px-3 py-2 cursor-pointer min-h-[2.5rem]">
                          <span className="text-xs font-semibold text-white">YouTube Shorts</span>
                          <span className="flex items-center gap-2 text-[11px] theme-text-secondary">
                            <input type="checkbox" checked={youtubeShortsMode} onChange={(e) => setYoutubeShortsMode(e.target.checked)} className="rounded shrink-0" />
                            Shorts hooks, tags, and #Shorts hashtags
                          </span>
                        </label>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={ytAiBusy}
                        onClick={handleYtAutofill}
                        className="rounded-full border border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.08] gap-1.5 w-full"
                        data-testid="sp-youtube-autofill-btn"
                      >
                        {ytAiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <TwinneeLogo className="w-4 h-4" />}
                        Autofill using AI
                      </Button>
                      {ytAutofillNote ? (
                        <p className="text-[11px] leading-snug theme-text-secondary">{ytAutofillNote}</p>
                      ) : null}
                    </div>

                    <p className="text-sm font-semibold text-white">Details</p>
                    <div className="space-y-2">
                      <Label htmlFor="sp-title">Title *</Label>
                      <Input id="sp-title" maxLength={100} value={title} onChange={(e) => setTitle(e.target.value)} className={fieldInputClass} style={fieldInputStyle} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sp-desc">Description</Label>
                      <textarea id="sp-desc" rows={4} maxLength={5000} value={description} onChange={(e) => setDescription(e.target.value)} className={fieldTextareaClass} />
                    </div>
                    <div className="space-y-2">
                      <Label>Tags</Label>
                      <PublishTagChips
                        value={tagsRaw}
                        suggestions={tagSuggestions}
                        onChange={setTagsRaw}
                        onSuggestionsChange={setTagSuggestions}
                        moreBusy={moreTagsBusy}
                        onMore={handleMoreTags}
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select value={categoryId} onValueChange={setCategoryId}>
                          <SelectTrigger className={fieldInputClass} style={fieldInputStyle}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {categories.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Privacy</Label>
                        <Select value={privacyStatus} onValueChange={setPrivacyStatus}>
                          <SelectTrigger className={fieldInputClass} style={fieldInputStyle}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="private">Private</SelectItem>
                            <SelectItem value="unlisted">Unlisted</SelectItem>
                            <SelectItem value="public">Public</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <PublishThumbnailPicker
                      platform="youtube"
                      videoUrl={videoUrl}
                      title={title}
                      description={description}
                      thumbnailUrl={thumbnailUrl}
                      onThumbnailUrlChange={setThumbnailUrl}
                      frameUrls={thumbFrameUrls}
                      onFrameUrlsChange={setThumbFrameUrls}
                      aiUrls={aiThumbUrls}
                      onAiUrlsChange={setAiThumbUrls}
                      showAiGenerateButton
                      helpText="Autofill using AI generates story thumbnails, or use AI story thumbnail, sample frames, upload an image, or paste a URL."
                      label="Thumbnail (JPEG/PNG)"
                      testIdPrefix="sp-youtube-thumb"
                    />
                    <details className="rounded-2xl bg-white/[0.04] px-4 py-3">
                      <summary className="text-sm font-semibold text-white cursor-pointer">More options</summary>
                      <div className="mt-4 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="sp-yt-lang">Default language</Label>
                        <Input id="sp-yt-lang" placeholder="en" value={defaultLanguage} onChange={(e) => setDefaultLanguage(e.target.value)} className={fieldInputClass} style={fieldInputStyle} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sp-yt-alang">Audio language</Label>
                        <Input id="sp-yt-alang" placeholder="en" value={defaultAudioLanguage} onChange={(e) => setDefaultAudioLanguage(e.target.value)} className={fieldInputClass} style={fieldInputStyle} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sp-yt-pl">Playlist ID (optional)</Label>
                      <Input id="sp-yt-pl" value={playlistId} onChange={(e) => setPlaylistId(e.target.value)} className={fieldInputClass} style={fieldInputStyle} />
                    </div>
                    <div className="space-y-2">
                      <Label>License</Label>
                      <Select value={license} onValueChange={setLicense}>
                        <SelectTrigger className={fieldInputClass} style={fieldInputStyle}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="youtube">Standard YouTube license</SelectItem>
                          <SelectItem value="creativeCommon">Creative Commons — Attribution</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-xs cursor-pointer theme-text-secondary">
                        <input type="checkbox" checked={notifySubscribers} onChange={(e) => setNotifySubscribers(e.target.checked)} className="rounded" />
                        Notify subscribers (when privacy is public)
                      </label>
                      <label className="flex items-center gap-2 text-xs cursor-pointer theme-text-secondary">
                        <input type="checkbox" checked={embeddable} onChange={(e) => setEmbeddable(e.target.checked)} className="rounded" />
                        Allow embedding
                      </label>
                      <label className="flex items-center gap-2 text-xs cursor-pointer theme-text-secondary">
                        <input type="checkbox" checked={madeForKids} onChange={(e) => setMadeForKids(e.target.checked)} className="rounded" />
                        Made for kids (accurate declaration required)
                      </label>
                    </div>
                      </div>
                    </details>
                    <div className="pt-1">
                      <Button type="submit" disabled={ytUploadBusy || !ytStatus.connected} className="rounded-full px-5 py-2.5 h-auto gap-2 bg-[#34d399] text-[#06211b] font-bold hover:brightness-105">
                        {ytUploadBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Youtube className="w-4 h-4" />}
                        Upload to YouTube
                      </Button>
                    </div>
                  </form>
                </div>
                <div className="flex flex-col gap-4 lg:self-stretch">
                  <SeoChecklist
                    className="lg:sticky lg:top-5 lg:max-h-[calc(100vh-2.5rem)] lg:overflow-y-auto"
                    title={title}
                    description={description}
                    tags={tagsRaw}
                    hasThumbnail={Boolean(thumbnailUrl.trim())}
                    language={defaultLanguage}
                    categoryId={hasPublishVideo ? categoryId : ''}
                    inPlaylist={hasPublishVideo && Boolean(playlistId.trim())}
                    privacy={hasPublishVideo ? privacyStatus : 'public'}
                  />
                  <SocialPublishHistoryPanel platform="youtube" items={ytHistory} loading={ytHistoryLoading} className="!border-0 !shadow-none" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="instagram" className="mt-4">
              <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(300px,380px)] items-start gap-5">
                <div className="flex flex-col min-w-0 gap-4">
                  {renderAccountBar({
                    platform: 'instagram',
                    loading: igStatusLoading,
                    connected: igStatus.connected,
                    connectedLabel: igStatus.username ? `@${igStatus.username}` : 'instagram',
                    onConnect: handleConnectInstagram,
                    onDisconnect: handleIgDisconnect,
                    connectBusy: igBeginBusy,
                    disconnectBusy: igDisconnectBusy,
                    connectLabel: 'Connect Instagram',
                    connectClassName: 'text-white border-0',
                    connectStyle: { background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743)' },
                    hint: 'Business or Creator account linked to a Facebook Page',
                  })}
                  {igStatus.connected && igStatus.page_name ? (
                    <p className="text-xs text-[#8b95a7] -mt-2">via {igStatus.page_name}</p>
                  ) : null}

                  <form onSubmit={handleInstagramUpload} className="flex flex-col gap-4">
                    <div className={aiOptionsClass} style={aiOptionsStyle}>
                      <p className="text-sm font-semibold text-white">Fill this in for me</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Caption language</Label>
                          <Select value={igLang} onValueChange={setIgLang}>
                            <SelectTrigger className={fieldInputClass} style={fieldInputStyle} data-testid="sp-instagram-metadata-lang">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="en">English</SelectItem>
                              <SelectItem value="hi">Hindi (हिंदी)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <label className="flex flex-col justify-center gap-1.5 rounded-xl bg-[#0c1018] px-3 py-2 cursor-pointer min-h-[2.5rem]">
                          <span className="text-xs font-semibold text-white">Reels mode</span>
                          <span className="flex items-center gap-2 text-[11px] theme-text-secondary">
                            <input type="checkbox" checked={igReelsMode} onChange={(e) => setIgReelsMode(e.target.checked)} className="rounded shrink-0" />
                            Reels hooks + hashtags in AI caption
                          </span>
                        </label>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={igAiBusy}
                        onClick={handleIgAutofill}
                        className="rounded-full border border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.08] gap-1.5 w-full"
                        data-testid="sp-instagram-autofill-btn"
                      >
                        {igAiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <TwinneeLogo className="w-4 h-4" />}
                        Autofill using AI
                      </Button>
                      {igAutofillNote ? (
                        <p className="text-[11px] leading-snug theme-text-secondary">{igAutofillNote}</p>
                      ) : null}
                    </div>

                    <p className="text-sm font-semibold text-white">Caption and cover</p>
                    <div className="space-y-2">
                      <Label htmlFor="sp-ig-caption">Caption * (max 2200)</Label>
                      <textarea
                        id="sp-ig-caption"
                        rows={5}
                        maxLength={2200}
                        value={igCaption}
                        onChange={(e) => setIgCaption(e.target.value)}
                        className={fieldTextareaClass}
                      />
                    </div>
                    <PublishThumbnailPicker
                      platform="instagram"
                      videoUrl={videoUrl}
                      title={(igCaption || title || '').trim().split('\n')[0]}
                      description={igCaption}
                      thumbnailUrl={igCoverUrl}
                      onThumbnailUrlChange={setIgCoverUrl}
                      frameUrls={igCoverFrameUrls}
                      onFrameUrlsChange={setIgCoverFrameUrls}
                      aiUrls={igAiThumbUrls}
                      onAiUrlsChange={setIgAiThumbUrls}
                      showAiGenerateButton
                      label="Reel cover image (optional)"
                      testIdPrefix="sp-instagram-cover"
                    />
                    <label className="flex items-center gap-2 text-sm cursor-pointer theme-text-secondary">
                      <input type="checkbox" checked={igShareToFeed} onChange={(e) => setIgShareToFeed(e.target.checked)} className="rounded" />
                      Share Reel to profile grid
                    </label>
                    <div className="pt-1">
                      <Button type="submit" disabled={igUploadBusy || !igStatus.connected} className="rounded-full px-5 py-2.5 h-auto gap-2 bg-[#34d399] text-[#06211b] font-bold hover:brightness-105">
                        {igUploadBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Instagram className="w-4 h-4" />}
                        Publish Reel
                      </Button>
                    </div>
                  </form>
                </div>
                <SocialPublishHistoryPanel platform="instagram" items={igHistory} loading={igHistoryLoading} className="!border-0 !shadow-none" />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
